/**
 * Global throttle applied to every event on its way out to Sentry, whatever its
 * origin: window.onerror, unhandledrejection, Angular's ErrorHandler or the HTTP
 * interceptor.
 *
 * The throttle in sentry-runtime-error.reporter.ts only covers errors that reach
 * Angular's ErrorHandler. Anything thrown outside the Angular zone bypasses it
 * and is captured raw by the SDK's globalHandlers integration: ResizeObserver
 * callbacks (zone.js patches MutationObserver and IntersectionObserver, but not
 * ResizeObserver), Capacitor plugin listeners invoked from native, and scripts
 * injected by the engine itself. One user produced 84 events in a single minute
 * through that path (WebKit InvalidStateError on /training/:id, iOS), so without
 * a gate at the transport boundary a single loop can drain the whole quota.
 *
 * Wired as `beforeSend`, which the SDK calls for error events only — transactions
 * go through `beforeSendTransaction`, so performance sampling is untouched.
 */

/** Same event signature is sent at most once within this window. */
const SIGNATURE_COOLDOWN_MS = 5 * 60 * 1000;
/**
 * Hard cap on the number of events sent per page session, across all paths. Also
 * bounds `lastSentBySignature`, since entries are only added when an event is
 * actually sent.
 */
const MAX_EVENTS_PER_SESSION = 50;

const lastSentBySignature = new Map<string, number>();
let sessionEventCount = 0;

/**
 * Minimal structural view of a Sentry error event, with only the fields the
 * signature is built from. Avoids depending on @sentry/core, which is not a
 * declared dependency of the app (the event type is not re-exported by
 * @sentry/angular).
 */
interface ISentryEventShape {
  message?: string;
  exception?: {
    values?: {
      type?: string;
      value?: string;
      stacktrace?: {
        frames?: { filename?: string; lineno?: number; colno?: number }[];
      };
    }[];
  };
}

/**
 * Builds a stable key for an event so repeats collapse while distinct bugs stay
 * apart: exception type + value + the throw site. Frames in the Sentry protocol
 * are ordered oldest first, so the throw site is the last one.
 */
function eventSignature(event: ISentryEventShape): string {
  const exception = event.exception?.values?.[0];
  if (!exception) return event.message ?? 'unknown';

  const frames = exception.stacktrace?.frames;
  const throwSite = frames?.[frames.length - 1];
  const location = throwSite
    ? `${throwSite.filename ?? ''}:${throwSite.lineno ?? ''}:${throwSite.colno ?? ''}`
    : '';

  return `${exception.type ?? ''}:${exception.value ?? ''}:${location}`;
}

/**
 * `beforeSend` hook: returns the event when it may be sent, or `null` to drop it.
 * Generic so the SDK's own event type flows through unchanged.
 */
export function throttleSentryEvent<T extends ISentryEventShape>(event: T): T | null {
  if (sessionEventCount >= MAX_EVENTS_PER_SESSION) return null;

  const signature = eventSignature(event);
  const now = Date.now();
  const lastSent = lastSentBySignature.get(signature);
  if (lastSent !== undefined && now - lastSent < SIGNATURE_COOLDOWN_MS) {
    return null;
  }

  lastSentBySignature.set(signature, now);
  sessionEventCount += 1;
  return event;
}
